import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save } from "lucide-react";

export default function AdminCopyrightRemoval() {
  const [active, setActive] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/merchant/config/copyright-removal").then(({ data }) => {
      setActive(Boolean(data.active));
      setCfg(data.copyright_removal || { hide: false, label: "" });
    }).catch((e) => toast.error(extractErr(e)));
  }, []);

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Copyright Removal</strong> from the App Store to hide or rename the MeraStartup footer credit.</Card>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/copyright-removal", cfg);
      setCfg(data.copyright_removal);
      toast.success("Footer credit saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const previewName = (cfg.label || "").trim() || "MeraStartup";

  return (
    <div className="max-w-2xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="cr-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Copyright Removal</h1>
        <p className="text-sm text-slate-500 mt-1">Hide “Powered by MeraStartup” on your storefront footer, or show your own brand name instead. Switch the addon off from App Store to restore the default.</p>
      </div>
      <Card className="p-6 space-y-5 border-slate-200/70">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="cr-hide">Hide powered-by credit</Label>
            <p className="text-xs text-slate-500 mt-0.5">Removes the line entirely from every theme footer.</p>
          </div>
          <Switch id="cr-hide" checked={Boolean(cfg.hide)} onCheckedChange={(hide) => setCfg({ ...cfg, hide })} data-testid="cr-hide" />
        </div>
        <div>
          <Label htmlFor="cr-label">Replace “MeraStartup” with</Label>
          <Input
            id="cr-label"
            className="mt-1"
            data-testid="cr-label"
            disabled={Boolean(cfg.hide)}
            maxLength={80}
            placeholder="Your brand name"
            value={cfg.label || ""}
            onChange={(e) => setCfg({ ...cfg, label: e.target.value })}
          />
          <p className="text-xs text-slate-500 mt-1">Leave blank to keep MeraStartup. Ignored while hide is on.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="cr-preview">
          {cfg.hide ? <span className="italic text-slate-400">Footer credit hidden</span> : <>Storefront preview: Powered by <span className="font-semibold text-slate-900">{previewName}</span></>}
        </div>
        <Button type="button" onClick={save} disabled={saving} data-testid="cr-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
        </Button>
      </Card>
    </div>
  );
}
